import axios, { AxiosInstance, AxiosError } from 'axios';
import { API_ENDPOINTS } from '@/config/constants';
import { 
  UserProfileResponse,
  BusinessCard,
  QRGenerateRequest, 
  QRGenerateResponse,
  CreateCardRequest,
  UpdateCardRequest,
  ListCardsResponse,
  CardAnalyticsResponse,
  SuccessResponse,
  ApiError,
  PublicProfileData,
  FeedbackSubmission,
  FeedbackResponse,
} from '@/types';

class ApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 seconds
    });
    
    // Add request interceptor for debugging
    this.axiosInstance.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    // Add response interceptor for debugging
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  private handleError(error: AxiosError<ApiError>): never {
    if (error.response) {
      const apiError = error.response.data;
      throw new Error(apiError.message || 'An error occurred');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }

  async getUserProfile(accessToken: string): Promise<UserProfileResponse> {
    try {
      const response = await this.axiosInstance.get(API_ENDPOINTS.getUserProfile, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error('Failed to retrieve user profile');
      }
    } catch (error) {
      return this.handleError(error as AxiosError<ApiError>);
    }
  }

  async generateQRCode(
    request: QRGenerateRequest,
    accessToken: string
  ): Promise<QRGenerateResponse> {
    try {
      const response = await this.axiosInstance.post(
        API_ENDPOINTS.generateQR,
        request,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error('Failed to generate QR code');
      }
    } catch (error) {
      return this.handleError(error as AxiosError<ApiError>);
    }
  }

  // ========== Card Management Endpoints ==========

  /**
   * List all cards for a user
   */
  async listCards(accessToken: string, includeInactive: boolean = false): Promise<ListCardsResponse> {
    try {
      const response = await this.axiosInstance.get(
        API_ENDPOINTS.listCards,
        { 
          params: { include_inactive: includeInactive },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error('Failed to retrieve cards');
      }
    } catch (error) {
      return this.handleError(error as AxiosError<ApiError>);
    }
  }

  /**
   * Get a single card by ID
   */
  async getCard(cardId: number, accessToken: string): Promise<BusinessCard> {
    try {
      const response = await this.axiosInstance.get(
        API_ENDPOINTS.getCard(cardId),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to retrieve card');
      }
    } catch (error) {
      return this.handleError(error as AxiosError<ApiError>);
    }
  }

  /**
   * Create a new card
   */
  async createCard(request: CreateCardRequest, accessToken: string): Promise<{ cardId: number }> {
    try {
      const response = await this.axiosInstance.post(
        API_ENDPOINTS.createCard,
        request,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to create card');
      }
    } catch (error) {
      const axiosError = error as AxiosError<ApiError>;
      
      // Handle card limit error specifically
      if (axiosError.response?.data?.error === 'CARD_LIMIT_REACHED') {
        const { message, currentCount, maxLimit } = axiosError.response.data;
        throw new Error(message || `Card limit reached (${currentCount}/${maxLimit}). Please delete an existing card to create a new one.`);
      }
      
      return this.handleError(axiosError);
    }
  }

  /**
   * Update an existing card
   */
  async updateCard(
    cardId: number,
    request: UpdateCardRequest,
    accessToken: string
  ): Promise<SuccessResponse> {
    try {
      const response = await this.axiosInstance.put(
        API_ENDPOINTS.updateCard(cardId),
        request,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error('Failed to update card');
      }
    } catch (error) {
      return this.handleError(error as AxiosError<ApiError>);
    }
  }

  /**
   * Delete a card permanently
   */
  async deleteCard(cardId: number, accessToken: string): Promise<SuccessResponse> {
    try {
      const response = await this.axiosInstance.delete(
        API_ENDPOINTS.deleteCard(cardId),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error('Failed to delete card');
      }
    } catch (error) {
      return this.handleError(error as AxiosError<ApiError>);
    }
  }

  /**
   * Set a card as default
   */
  async setDefaultCard(cardId: number, accessToken: string): Promise<SuccessResponse> {
    try {
      const response = await this.axiosInstance.post(
        API_ENDPOINTS.setDefaultCard(cardId),
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error('Failed to set default card');
      }
    } catch (error) {
      return this.handleError(error as AxiosError<ApiError>);
    }
  }

  /**
   * Deactivate a card (soft delete)
   */
  async deactivateCard(cardId: number, accessToken: string): Promise<SuccessResponse> {
    try {
      const response = await this.axiosInstance.post(
        API_ENDPOINTS.deactivateCard(cardId),
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error('Failed to deactivate card');
      }
    } catch (error) {
      return this.handleError(error as AxiosError<ApiError>);
    }
  }

  // ========== Feedback ==========

  /**
   * Submit user feedback. Only overall_rating is required.
   * app_version is read from import.meta.env.VITE_APP_VERSION and sent as a header.
   */
  async submitFeedback(submission: FeedbackSubmission, accessToken: string): Promise<FeedbackResponse> {
    try {
      const appVersion = import.meta.env.VITE_APP_VERSION as string | undefined;
      const response = await this.axiosInstance.post(
        API_ENDPOINTS.submitFeedback,
        submission,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            ...(appVersion ? { 'X-App-Version': appVersion } : {}),
          },
        }
      );
      return response.data;
    } catch (error) {
      return this.handleError(error as AxiosError<ApiError>);
    }
  }
}

export const apiService = new ApiService();


